import React, { useEffect, useState, useMemo, useCallback } from "react";
import MetricsCard from "./MetricsCard";
import SummaryCard from "./SummaryCard";

const STATUS_CONFIG = [
  {
    label: "New Applications",
    status: "New",
    icon: "bi bi-plus-circle",
    colorClass: "text-primary",
    bgColor: "#0000001a",
  },
  {
    label: "Interviews",
    status: "For Interview",
    icon: "bi bi-person-video",
    colorClass: "text-info",
    bgColor: "#0dcaf01a",
  },
  {
    label: "For Coordinators",
    status: "For Coordinator",
    icon: "bi bi-person-check",
    colorClass: "text-warning",
    bgColor: "#fbd2181a",
  },
  {
    label: "On-boarding",
    status: "On-boarding",
    icon: "bi bi-clipboard-check",
    colorClass: "text-secondary",
    bgColor: "#0000003b",
  },
  {
    label: "Hired",
    status: "Hired",
    icon: "bi bi-check-circle",
    colorClass: "text-success",
    bgColor: "#8bc34b1a",
  },
  {
    label: "Rejected",
    status: "Not Hired",
    icon: "bi bi-x-circle",
    colorClass: "text-danger",
    bgColor: "#d011271a",
  },
];

const MetricsContainer = ({
  objects,
  onClickStatus,
  progress,
  metricsData,
}) => {
  const statuses = useMemo(() => {
    if (!metricsData || typeof metricsData !== "object") {
      return STATUS_CONFIG.map((status) => ({ ...status, count: 0 }));
    }

    return STATUS_CONFIG.map((status) => ({
      ...status,
      count: metricsData[status.status] || 0,
    }));
  }, [metricsData]);

  const totalApplications = useMemo(
    () => statuses.reduce((sum, status) => sum + (status.count || 0), 0),
    [statuses]
  );

  const inProgress = useMemo(
    () =>
      statuses
        .slice(1, 4)
        .reduce((sum, status) => sum + (status.count || 0), 0),
    [statuses]
  );

  const hired = useMemo(
    () => statuses.find((s) => s.status === "Hired")?.count || 0,
    [statuses]
  );

  const successRate = useMemo(
    () => (totalApplications > 0 ? (hired / totalApplications) * 100 : 0),
    [hired, totalApplications]
  );

  const conversionRate = useMemo(
    () => ((inProgress + hired) / Math.max(totalApplications, 1)) * 100,
    [inProgress, hired, totalApplications]
  );

  const handleStatusClick = useCallback(
    (status) => {
      if (onClickStatus) onClickStatus(status);
    },
    [onClickStatus]
  );

  return (
    <div className="container-fluid">
      <div className="py-2">
        <div className="row g-3 mb-2">
          {statuses.map((status, index) => (
            <div key={index} className="col-12 col-sm-6 col-lg-4 col-xl-2">
              <MetricsCard
                progress={progress}
                status={status.label}
                count={status.count}
                icon={status.icon}
                bgColor={status.bgColor}
                colorClass={status.colorClass}
                onClick={() => handleStatusClick(status.status)}
              />
            </div>
          ))}
        </div>

        <div className="row g-3 my-2">
          <div className="col-12">
            <div
              className="card border-0 bg-white"
              style={{ borderRadius: "12px" }}
            >
              <div className="card-body p-0">
                <div className="row g-0">
                  <div className="col-12 col-md-3 border-end">
                    <SummaryCard
                      title="Total Applications"
                      progress={progress}
                      value={totalApplications}
                      isPositive={true}
                    />
                  </div>
                  <div className="col-12 col-md-3 border-end">
                    <SummaryCard
                      title="In Process"
                      progress={progress}
                      value={inProgress}
                      isPositive={true}
                    />
                  </div>
                  <div className="col-12 col-md-3 border-end">
                    <SummaryCard
                      title="Success Rate"
                      progress={progress}
                      value={`${successRate.toFixed(2)}%`}
                      isPositive={true}
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <SummaryCard
                      title="Conversion"
                      progress={progress}
                      value={`${conversionRate.toFixed(2)}%`}
                      isPositive={conversionRate >= 50}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .metrics-card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .metrics-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
        }
        .border-end {
          border-right: 1px solid #e2e8f0 !important;
        }
        @media (max-width: 768px) {
          .border-end {
            border-right: none !important;
            border-bottom: 1px solid #e2e8f0 !important;
          }
          .border-end:last-child {
            border-bottom: none !important;
          }
        }
        .btn {
          font-weight: 500;
          font-size: 0.875rem;
        }
        .card {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }
        h1,
        h2,
        h3,
        h4 {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui,
            sans-serif;
        }
      `}</style>
    </div>
  );
};

export default MetricsContainer;
